/**
 * Admin invite-code queries — list codes, batch-generate single-use codes,
 * create group codes.
 *
 * NOTE on DB naming: the underlying tables (`beta_invites`,
 * `beta_invite_redemptions`) and RPCs (`redeem_beta_invite`) retain the
 * `beta_*` prefix from when this app had a beta tier. The beta tier was
 * retired 2026-04-30 in favor of free + premium, but the DB internals stay
 * for stability — they're never user-visible. A planned migration will
 * rename them; see the `rename_beta_db_objects` task in roadmapData.js.
 */
import { supabase } from '@/lib/supabase'
import { ADMIN_INVITE_CODES_LIMIT } from '../_constants'

/** Load invite codes (paginated). */
export async function fetchInviteCodes({ limit = ADMIN_INVITE_CODES_LIMIT, offset = 0 } = {}) {
  const { data, error } = await supabase
    .from('beta_invites')
    .select('id, code, max_uses, use_count, group_name, expires_at, created_at, used_by')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)
  if (error) throw error
  return data || []
}

/** Generate and insert new single-use invite codes. */
export async function insertInviteCodes(codes) {
  const { error } = await supabase.from('beta_invites').insert(codes)
  if (error) throw error
}

/**
 * Insert a single group invite code. Throws a specific "Code already exists."
 * error on unique-constraint violation so the admin UI can surface it cleanly.
 */
export async function insertGroupInvite({ code, groupName, maxUses, expiresAt }) {
  const { error } = await supabase.from('beta_invites').insert({
    code,
    group_name: groupName,
    max_uses: maxUses,
    expires_at: expiresAt,
  })
  if (error) {
    if (error.code === '23505') throw new Error('Code already exists.')
    throw error
  }
}
